import { Input, Select, BankAccountInput } from '@new-components/elements';
import { useFormContext } from '@hooks/react-hook-form';
import { useDisclosure } from '@hooks/use-disclosure';
import {
  ApplicationDataResponse,
  useRGetApplicationData,
  useRVerifyBankAccount,
} from '@repositories/new-application';

import ApplicationCard from '../../../../../application-card';
import SectionTitle from '../../../../../section-title';

import DialogBankAccountValidation from '../../dialogs/bank-account-validation';
import { AccountInformationProps } from './types';

export const BankAccountForm = ({
  isError,
  refetchData,
  readonly,
  loading,
  defaultIsVerifiedAccount,
  bankOptions,
}: {
  readonly?: boolean;
  isError: boolean;
  loading: boolean;
  refetchData: () => void;
  defaultIsVerifiedAccount?: boolean;
  bankOptions: string[];
}) => {
  const {
    fieldsRef,
    watch,
    setError,
    formState: { isSubmitting, isSubmitSuccessful },
    getValues,
    trigger,
    setValue,
  } = useFormContext();

  const dialogBankAccountVerification = useDisclosure();

  const {
    mutate,
    isVerifiedBankAccount,
    isInvalidBankAccount,
    isLoading: isLoadingVerifyBankAccount,
    isValidatedBankAccount,
  } = useRVerifyBankAccount({
    onSuccess: () => {
      setValue('bankAccountNumberVerified', true);
      dialogBankAccountVerification.onClose();
    },
    onError: (err) => {
      const payload = (err.payload || {
        statusCode: 400,
        errors: [],
      }) as {
        statusCode: number;
        errors: string[];
      };
      const statusCode = payload.statusCode;
      setValue('bankAccountNumberVerified', false);
      switch (statusCode) {
        case 400:
          dialogBankAccountVerification.onClose();
          setTimeout(() => {
            setError('bankAccountNumber', {
              message: payload.errors[0],
            });
          }, 100);
          break;

        default:
          break;
      }
    },
  });

  function handleVerify() {
    mutate({
      variables: {
        bankAccountNumber: getValues('bankAccountNumber'),
        bankName: getValues('bankName'),
      },
    });
  }

  return (
    <>
      <Select
        ref={(e) => (fieldsRef.current['bankName'] = e)}
        disabled={readonly || loading || (isSubmitting && !isSubmitSuccessful)}
        onChange={() => {
          setValue('bankAccountNumberVerified', false);
        }}
        onFocus={() => isError && refetchData()}
        name='bankName'
        label='Nama Bank'
        placeholder='Pilih bank'
        options={bankOptions || []}
      />
      <BankAccountInput
        ref={(e) => (fieldsRef.current['bankAccountNumber'] = e)}
        disabled={
          readonly ||
          !watch('bankName') ||
          (isSubmitting && !isSubmitSuccessful)
        }
        name='bankAccountNumber'
        label='Nomor Rekening Pribadi'
        disableVerify={!watch('bankAccountNumber')}
        onVerify={dialogBankAccountVerification.onOpen}
        onChange={() => {
          if (typeof watch('bankAccountNumberVerified') !== 'undefined') {
            trigger('bankAccountNumber');
          }
          setValue('bankAccountNumberVerified', false);
        }}
        isVerified={
          readonly ??
          watch('bankAccountNumberVerified') ??
          defaultIsVerifiedAccount ??
          isVerifiedBankAccount
        }
        isInvalid={isInvalidBankAccount}
        isValidated={
          watch('bankAccountNumberVerified') ?? isValidatedBankAccount
        }
      />

      <DialogBankAccountValidation
        isOpen={dialogBankAccountVerification.isOpen}
        accountName={getValues('fullname')}
        bankAccount={watch('bankAccountNumber')}
        bankName={watch('bankName')}
        onCancel={() => {
          setValue('bankAccountNumberVerified', false);
          dialogBankAccountVerification.onClose();
          setError('bankAccountNumber', {
            message: 'Pastikan nomor rekening atas nama kamu pribadi',
          });
        }}
        onSecondCancel={() => {
          setValue('bankAccountNumberVerified', false);
          dialogBankAccountVerification.onClose();
          setError('bankAccountNumber', {
            message:
              'Pastikan rekening kamu aktif atau pernah melakukan transaksi dalam 3 bulan terakhir',
          });
        }}
        onVerify={handleVerify}
        isLoading={isLoadingVerifyBankAccount}
      />
    </>
  );
};

const AccountInformation = (props: AccountInformationProps) => {
  const { readonly, defaultIsVerifiedAccount } = props;

  const {
    fieldsRef,
    formState: { isSubmitting, isSubmitSuccessful },
  } = useFormContext();

  const {
    data,
    isLoading: isLoadingGetApplicationData,
    isError: isErrorGetApplicationData,
    refetch: refetchGetApplicationData,
  } = useRGetApplicationData<ApplicationDataResponse<3>>({
    page: 3,
  });

  return (
    <ApplicationCard>
      <SectionTitle
        title='Informasi Rekening'
        subtitle={
          !props.readonly
            ? 'Mohon masukan nomor rekening yang terdaftar di Grab'
            : ''
        }
      />

      <BankAccountForm
        readonly={readonly}
        isError={isErrorGetApplicationData}
        refetchData={refetchGetApplicationData}
        defaultIsVerifiedAccount={defaultIsVerifiedAccount}
        loading={isLoadingGetApplicationData}
        bankOptions={data?.data.banks || []}
      />

      <Select
        ref={(e) => (fieldsRef.current['loanPurpose'] = e)}
        disabled={
          readonly ||
          isLoadingGetApplicationData ||
          (isSubmitting && !isSubmitSuccessful)
        }
        name='loanPurpose'
        label='Tujuan Peminjaman'
        placeholder='pilih'
        options={data?.data.loanPurposes}
      />
      <Input
        ref={(e) => (fieldsRef.current['referalCode'] = e)}
        disabled={readonly || (isSubmitting && !isSubmitSuccessful)}
        name='referralCode'
        label='Kode Referral (Tidak Wajib)'
        placeholder={!readonly ? 'Kode Referral' : ''}
      />
    </ApplicationCard>
  );
};

export default AccountInformation;
